import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { useAuth } from '../app/providers/auth';
import { clearCurrentTenantId } from '../store/tenantStorage';
import { useResolvedAuthProviderType } from './useResolvedAuthProviderType';
import { useUserTenants } from './api/useUserTenants';
import { shouldSignOutUnsupportedTorchTenant } from './should-sign-out-unsupported-torch-tenant';
import {
  queueTorchNotSupportedToast,
  TORCH_NOT_SUPPORTED_TOAST_DURATION_MS,
} from './torch-not-supported-toast';

export function useRequireTorchSupportedTenant({
  enabled,
}: {
  enabled: boolean;
}): void {
  const router = useRouter();
  const { logout } = useAuth();
  const t = useTranslations('signIn');
  const authProviderType = useResolvedAuthProviderType();
  const {
    tenants,
    currentTenantId,
    isTerraUser,
    hasAccessToTerraTenant,
    isFetched,
    error,
  } = useUserTenants({ enabled });
  const isSigningOutRef = useRef(false);

  useEffect(() => {
    if (isSigningOutRef.current) {
      return;
    }

    const shouldSignOut = shouldSignOutUnsupportedTorchTenant({
      isFetched,
      hasError: Boolean(error),
      isTerraUser,
      hasAccessToTerraTenant,
      skipCheck:
        !enabled || authProviderType === null || authProviderType === 'local',
      currentTenantId,
      tenantIds: new Set(tenants.map((tenant) => tenant.id)),
    });

    if (!shouldSignOut) {
      return;
    }

    isSigningOutRef.current = true;
    queueTorchNotSupportedToast();
    toasterApi.error({
      title: t('toasts.torchNotSupported.title'),
      description: t('toasts.torchNotSupported.description'),
      duration: TORCH_NOT_SUPPORTED_TOAST_DURATION_MS,
    });

    const signOut = async () => {
      clearCurrentTenantId();

      try {
        await logout();
      } catch (logoutError: unknown) {
        console.error(
          'Failed to sign out unsupported Torch tenant',
          logoutError,
        );
        router.push('/sign-in');
      }
    };

    void signOut();
  }, [
    authProviderType,
    currentTenantId,
    enabled,
    error,
    hasAccessToTerraTenant,
    isFetched,
    isTerraUser,
    logout,
    router,
    t,
    tenants,
  ]);
}
